import { Disclosure } from '@headlessui/react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import useTranslation from '../../../../hooks/useTranslations';
import { selectActiveBusinessAreas } from '../../../../redux/modules/data/businessAreasSlice';
import { selectFolderById, selectFoldersByIds } from '../../../../redux/modules/data/foldersSlice';
import { selectPropertyById } from '../../../../redux/modules/data/propertiesSlice';
import { ArrowIcon } from '../../../misc/icons/Icons';

const Menu = ({ activeTree }) => {
	const activeBusinessAreas = useSelector((state) => selectActiveBusinessAreas(state));
	const t = useTranslation();

	if (activeTree?.folder3 && activeTree?.property) {
		return (
			<nav className="flex-1 pb-60">
				{activeTree.folder3.properties.map((property) => (
					<PropertyItem key={property.id} activeTree={activeTree} propertyId={property.id} />
				))}
			</nav>
		);
	}
	if (activeTree?.folder2 && activeTree?.property) {
		return (
			<nav className="flex-1 pb-60">
				{activeTree.folder2.properties.map((property) => (
					<PropertyItem key={property.id} activeTree={activeTree} propertyId={property.id} />
				))}
			</nav>
		);
	}
	if (activeTree?.folder2) {
		return (
			<nav className="flex-1 pb-60">
				{activeTree.folder2.children.map((subFolderId) => (
					<MenuSubItem key={subFolderId} activeTree={activeTree} subFolderId={subFolderId} />
				))}
			</nav>
		);
	}
	if (activeTree?.businessArea) {
		return (
			<nav className="flex-1 pb-60">
				<MenuItems activeTree={activeTree} folderIds={activeTree.businessArea.children} />
			</nav>
		);
	}

	if (activeBusinessAreas) {
		return (
			<nav className="flex-1 pb-60">
				{activeBusinessAreas.map((businessArea) => <NavItemLink key={businessArea.id} route={`/${businessArea.id}`} title={t(businessArea.translationkey)} />)}
			</nav>
		);
	}

	return null;
};

export default Menu;

const MenuItems = ({ activeTree, folderIds }) => {
	const folders = useSelector((state) => selectFoldersByIds(state, folderIds));
	const t = useTranslation();

	/* eslint-disable no-param-reassign */
	const groupedFolders = folders.filter(Boolean).filter((item) => (!item.isYellowMenuFolder || item.isYellowMenuFolder === '0') && (!item.isEvaluationapp || item.isEvaluationapp === '0')).reduce((result, item) => {
		const groupLabel = item.group_label || 'empty';
		const groupIndex = result.findIndex((group) => group.label === groupLabel);
		if (groupIndex === -1) {
			result.push({
				label: groupLabel,
				items: [item],
			});
		} else {
			result[groupIndex].items.push(item);
		}
		return result;
	}, []);
	/* eslint-enable no-param-reassign */

	return (
		<>
			{groupedFolders.map((folderGroup) => (
				<>
					{folderGroup.label !== 'empty' && (
						<div className="flex items-center w-full font-bold text-[1.0625rem] text-left text-white px-8 h-10 leading-5 underline">
							{t(folderGroup.label)}
						</div>
					)}
					{folderGroup.items.map((folder) => (
						<MenuItem key={folder.id} activeTree={activeTree} folderId={folder.id} />
					))}
				</>
			))}
		</>
	);
};

const MenuItem = ({ activeTree, folderId }) => {
	const folder = useSelector((state) => selectFolderById(state, folderId));
	const t = useTranslation();

	if (folder && folder?.isEvaluationapp !== '1' && folder?.isYellowMenuFolder !== '1') {
		return (
			<Disclosure>
				{({ open }) => (
					<>
						<Disclosure.Button
							className="w-full font-bold flex justify-between items-center text-[1.0625rem] text-left text-white can-hover:hover:text-oly-gray-darkest can-hover:hover:bg-white can-hover:hover:shadow-2xl focus-visible:shadow-2xl focus-visible:text-oly-gray-darkest focus-visible:bg-white focus:outline-none my-2 px-8 h-10 leading-5"
						// className={`w-full font-bold flex justify-between items-center text-[1.0625rem] text-left ${open ? 'text-oly-gray-darkest bg-white shadow-2xl' : 'text-white can-hover:hover:text-oly-gray-darkest can-hover:hover:bg-white can-hover:hover:shadow-2xl focus-visible:shadow-2xl focus-visible:text-oly-gray-darkest focus-visible:bg-white'} focus:outline-none my-2 px-8 h-10 leading-5`}
						>
							<span className="px-4">{t(folder.translationkey)}</span>
							<ArrowIcon className={`flex-shrink-0 w-5 h-5 transform duration-300 ${open ? '-rotate-180' : 'rotate-0'}`} />
						</Disclosure.Button>
						<Disclosure.Panel>
							{folder.children.map((subFolderId) => (
								<MenuSubItem key={subFolderId} activeTree={activeTree} folder={folder} subFolderId={subFolderId} />
							))}
						</Disclosure.Panel>
					</>
				)}

			</Disclosure>
		);
	}

	return null;
};

const PropertyItem = ({ activeTree, propertyId }) => {
	const t = useTranslation();

	const property = useSelector((state) => selectPropertyById(state, propertyId));

	let url = '';
	if (activeTree.businessArea && activeTree.folder1 && activeTree.folder2 && activeTree.folder3 && property) {
		url = `/${activeTree.businessArea.id}/${activeTree.folder1.id}/${activeTree.folder2.id}/${activeTree.folder3.id}/in-${property.id}`;
	} else if (activeTree.businessArea && activeTree.folder1 && activeTree.folder2 && property) {
		url = `/${activeTree.businessArea.id}/${activeTree.folder1.id}/${activeTree.folder2.id}/in-${property.id}`;
	}

	if (url) {
		return <NavItemLink route={url} title={t(property.translationkey)} noArrow active={property.id === activeTree.property?.id} />;
	}

	return null;
};

const MenuSubItem = ({ activeTree, folder, subFolderId }) => {
	const t = useTranslation();

	const subFolder = useSelector((state) => selectFolderById(state, subFolderId));

	let url = '';
	if (activeTree.businessArea && activeTree.folder1 && activeTree.folder2 && subFolder) {
		url = `/${activeTree.businessArea.id}/${activeTree.folder1.id}/${activeTree.folder2.id}/${subFolder.id}`;
	} else if (activeTree.businessArea && folder && subFolder) {
		url = `/${activeTree.businessArea.id}/${folder.id}/${subFolder.id}`;
	}

	if (url) {
		return <NavItemLink route={url} title={t(subFolder.translationkey)} classes="ml-3" />;
	}

	return null;
};

const NavItemLink = ({
	title, route, active = false, noArrow = false, classes = '',
}) => {
	const history = useHistory();

	const pushRoute = (e) => {
		e.preventDefault();
		history.push(route);
	};

	return (
		<a
			href={route}
			onClick={(e) => pushRoute(e)}
			className={`w-full flex justify-between items-center text-[1.0625rem] text-left text-white can-hover:hover:text-oly-gray-darkest can-hover:hover:bg-white can-hover:hover:shadow-2xl focus-visible:shadow-2xl focus-visible:text-oly-gray-darkest focus-visible:bg-white focus:outline-none my-2 px-8 h-10 leading-5 ${active ? '!text-oly-gray-darkest bg-white shadow-2xl' : ''}`}
		>
			<span className={`${classes} px-4`}>{title}</span>
			{!noArrow && <ArrowIcon className="flex-shrink-0 w-5 h-5 transform -rotate-90" />}
		</a>
	);
};
